// This replaces the direct Anthropic API call in the component
// It calls our Cloudflare Worker instead for secure API key handling

export async function requestAiAnalysis(analysis) {
  // Prepare condensed script data to fit in context
  const scriptSummary = {
    overview: {
      totalPages: analysis.totalPages,
      totalScenes: analysis.totalScenes,
      characterCount: analysis.characters.length,
      locationCount: analysis.locations.length
    },
    topCharacters: analysis.characters.slice(0, 8).map(c => ({
      name: c.name,
      lines: c.lines,
      scenes: c.sceneCount
    })),
    topLocations: analysis.locations.slice(0, 6).map(l => ({
      name: l.name,
      count: l.count,
      type: l.type
    })),
    openingScenes: analysis.scenes.slice(0, 3).map(s => ({
      header: s.header,
      intensity: s.intensityScore,
      characters: Array.from(s.characters).slice(0, 3),
      snippet: s.text.slice(0, 3).join(' ').substring(0, 200)
    })),
    midpointScene: (() => {
      const mid = analysis.scenes[Math.floor(analysis.scenes.length / 2)];
      return mid ? {
        header: mid.header,
        intensity: mid.intensityScore,
        characters: Array.from(mid.characters).slice(0, 3),
        snippet: mid.text.slice(0, 3).join(' ').substring(0, 200)
      } : null;
    })(),
    closingScenes: analysis.scenes.slice(-2).map(s => ({
      header: s.header,
      intensity: s.intensityScore,
      characters: Array.from(s.characters).slice(0, 3),
      snippet: s.text.slice(0, 3).join(' ').substring(0, 200)
    })),
    intensityArc: {
      act1Avg: Math.round(analysis.scenes.slice(0, Math.floor(analysis.scenes.length * 0.25))
        .reduce((a, s) => a + s.intensityScore, 0) / (analysis.scenes.length * 0.25) * 10) / 10,
      act2Avg: Math.round(analysis.scenes.slice(Math.floor(analysis.scenes.length * 0.25), Math.floor(analysis.scenes.length * 0.75))
        .reduce((a, s) => a + s.intensityScore, 0) / (analysis.scenes.length * 0.5) * 10) / 10,
      act3Avg: Math.round(analysis.scenes.slice(Math.floor(analysis.scenes.length * 0.75))
        .reduce((a, s) => a + s.intensityScore, 0) / (analysis.scenes.length * 0.25) * 10) / 10,
      peakScene: analysis.scenes.reduce((max, s) => s.intensityScore > max.intensityScore ? s : max, analysis.scenes[0])?.header
    },
    existingFeedback: {
      professorNotes: analysis.professorFeedback.map(f => f.text).slice(0, 3),
      snyderWarnings: analysis.snyderFeedback.filter(f => f.status === 'warning').map(f => f.beat)
    }
  };

  const prompt = `You are an expert screenplay consultant combining the analytical rigor of Robert McKee with the commercial instincts of Blake Snyder. You've read thousands of scripts and consulted on major studio films.

Analyze this screenplay data and provide SPECIFIC, ACTIONABLE feedback. Don't be generic - reference actual scene headers, character names, and page numbers from the data.

SCRIPT DATA:
${JSON.stringify(scriptSummary, null, 2)}

EXISTING TOOL FEEDBACK (for context, don't repeat these):
- Professor notes: ${scriptSummary.existingFeedback.professorNotes.join('; ')}
- Snyder warnings on beats: ${scriptSummary.existingFeedback.snyderWarnings.join(', ')}

Provide your analysis in this exact JSON format:
{
  "overallGrade": "A/B+/B/C+/C/D/F",
  "headline": "One punchy sentence capturing the script's biggest strength or weakness",
  "commercialPotential": "High/Medium/Low with brief reason",
  "insights": [
    {
      "category": "Structure|Character|Pacing|Opening|Ending|Dialogue|Theme",
      "severity": "critical|warning|suggestion|praise",
      "title": "Brief title (5 words max)",
      "finding": "What you observed in the data (be specific, cite scenes/characters)",
      "recommendation": "Actionable fix or enhancement"
    }
  ],
  "genreGuess": "Your best guess at genre based on locations, intensity, keywords",
  "comparables": ["Movie 1", "Movie 2"]
}

Provide 4-6 insights. At least one should be praise (something working well). Be direct and confident - screenwriters want honest feedback, not hedging.`;

  const response = await fetch('/api/analyze', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      scriptSummary,
      prompt
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'AI analysis request failed');
  }

  const data = await response.json();

  // Safely extract response text
  if (!data.content || !data.content[0] || !data.content[0].text) {
    throw new Error('Invalid response from AI - missing analysis data');
  }

  // Parse the response - clean markdown code blocks
  let responseText = data.content[0].text;
  responseText = responseText.replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();

  // Try to parse JSON with error handling
  try {
    return JSON.parse(responseText);
  } catch (parseError) {
    console.error('Failed to parse AI analysis JSON:', parseError);
    console.error('Response text:', responseText.substring(0, 500));

    // Try to find JSON object in the response
    const jsonMatch = responseText.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      try {
        return JSON.parse(jsonMatch[0]);
      } catch (secondError) {
        throw new Error('AI returned invalid JSON format. Please try again.');
      }
    }

    throw new Error('AI returned invalid JSON format. Please try again.');
  }
}

// NEW: Request 4-framework analysis (8-Sequence, Save the Cat, M.I.C.E., McKee)
export async function requestFrameworkAnalysis(analysis) {
  const scriptSummary = {
    totalPages: analysis.totalPages,
    totalScenes: analysis.totalScenes,
    characters: analysis.characters.slice(0, 8).map(c => c.name),
    scenes: analysis.scenes.map((s, idx) => ({
      page: Math.floor(idx * (analysis.totalPages / analysis.totalScenes)) + 1,
      header: s.header,
      intensity: s.intensityScore,
      characters: Array.from(s.characters).slice(0, 3)
    }))
  };

  const prompt = `Analyze this ${scriptSummary.totalPages}-page screenplay. Return ONLY JSON (no markdown).

CRITICAL:
- Set health to "warning" or "poor" for weak/missing beats (be honest!)
- Keep descriptions under 5 words
- Adjust page numbers to match script structure

{"analyses":[{"framework":"save-the-cat","beats":[{"beat":{"name":"Opening Image","description":"world"},"startPage":1,"endPage":1,"health":"good","notes":"ok"},{"beat":{"name":"Theme Stated","description":"theme"},"startPage":5,"endPage":5,"health":"good","notes":"ok"},{"beat":{"name":"Setup","description":"setup"},"startPage":1,"endPage":10,"health":"good","notes":"ok"},{"beat":{"name":"Catalyst","description":"catalyst"},"startPage":12,"endPage":12,"health":"good","notes":"ok"},{"beat":{"name":"Debate","description":"debate"},"startPage":12,"endPage":25,"health":"good","notes":"ok"},{"beat":{"name":"Break into Two","description":"decision"},"startPage":25,"endPage":25,"health":"good","notes":"ok"},{"beat":{"name":"B Story","description":"subplot"},"startPage":30,"endPage":30,"health":"good","notes":"ok"},{"beat":{"name":"Fun and Games","description":"promise"},"startPage":30,"endPage":55,"health":"good","notes":"ok"},{"beat":{"name":"Midpoint","description":"false high"},"startPage":55,"endPage":55,"health":"good","notes":"ok"},{"beat":{"name":"Bad Guys Close In","description":"pressure"},"startPage":55,"endPage":75,"health":"good","notes":"ok"},{"beat":{"name":"All Is Lost","description":"defeat"},"startPage":75,"endPage":75,"health":"good","notes":"ok"},{"beat":{"name":"Dark Night","description":"despair"},"startPage":75,"endPage":85,"health":"good","notes":"ok"},{"beat":{"name":"Break into Three","description":"solution"},"startPage":85,"endPage":85,"health":"good","notes":"ok"},{"beat":{"name":"Finale","description":"climax"},"startPage":85,"endPage":${scriptSummary.totalPages - 1},"health":"good","notes":"ok"},{"beat":{"name":"Final Image","description":"new world"},"startPage":${scriptSummary.totalPages},"endPage":${scriptSummary.totalPages},"health":"good","notes":"ok"}]},{"framework":"mice","threads":[{"type":"character","description":"change","openPage":1,"closePage":${scriptSummary.totalPages},"health":"good"},{"type":"idea","description":"question","openPage":1,"closePage":${scriptSummary.totalPages},"health":"good"}]},{"framework":"mckee","valueProgression":[{"act":1,"startValue":"stable","endValue":"disrupted","arcType":"positive","keyScenes":["opening","catalyst"]},{"act":2,"startValue":"rising","endValue":"defeated","arcType":"negative","keyScenes":["midpoint","low"]},{"act":3,"startValue":"renewed","endValue":"victory","arcType":"positive","keyScenes":["climax","resolution"]}]}]}

Analyze and update health values honestly.`;

  const response = await fetch('/api/analyze', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Framework analysis request failed');
  }

  const data = await response.json();

  // Safely extract response text
  if (!data.content || !data.content[0] || !data.content[0].text) {
    throw new Error('Invalid response from AI - missing analysis data');
  }

  // Parse the response - clean markdown code blocks and extra whitespace
  let responseText = data.content[0].text;

  // Remove markdown code blocks
  responseText = responseText.replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();

  // Remove any leading/trailing text before/after JSON
  const jsonStart = responseText.indexOf('{');
  const jsonEnd = responseText.lastIndexOf('}');
  if (jsonStart !== -1 && jsonEnd !== -1) {
    responseText = responseText.substring(jsonStart, jsonEnd + 1);
  }

  // Try to parse JSON, with better error handling
  try {
    const parsed = JSON.parse(responseText);
    // Validate structure
    if (!parsed.analyses || !Array.isArray(parsed.analyses)) {
      throw new Error('Invalid structure: missing analyses array');
    }
    return parsed;
  } catch (parseError) {
    console.error('Failed to parse framework analysis JSON:', parseError);
    console.error('Response text (first 1000 chars):', responseText.substring(0, 1000));
    console.error('Response text (last 500 chars):', responseText.substring(responseText.length - 500));

    // Try more aggressive JSON extraction
    const jsonMatch = responseText.match(/\{\s*"analyses"[\s\S]*\}/);
    if (jsonMatch) {
      try {
        const parsed = JSON.parse(jsonMatch[0]);
        if (parsed.analyses && Array.isArray(parsed.analyses)) {
          return parsed;
        }
      } catch (secondError) {
        console.error('Second parse attempt failed:', secondError);
      }
    }

    throw new Error('AI returned invalid JSON format. Please try generating the analysis again, or check the browser console for details.');
  }
}
